/**
 * 문서 레지스트리 — "1기능 = 1문서 페이지" (Claude Docs 구조, 2026-07-04 사용자 결정).
 * 기능 확장 시 여기 항목 하나 추가가 전부: {slug, group, title, tocItems?, body}.
 * URL: /?page=guide&doc=<slug>. 앵커 id(sec-N)는 가이드 QA(/api/guide/qa)의 근거 칩
 * 하위호환용으로 유지 — ANCHOR_TO_DOC 이 구 앵커를 문서로 매핑.
 */
import React from "react";

export const S = {
  page: { maxWidth: 880, color: "#1f2937",
    fontFamily: "'Inter','Malgun Gothic', '맑은 고딕', 'Pretendard',sans-serif", lineHeight: 1.65 },
  h1: { fontSize: 28, fontWeight: 800, marginBottom: 6 },
  h2: { fontSize: 20, fontWeight: 700, margin: "36px 0 10px", borderBottom: "1px solid #e5e7eb", paddingBottom: 6, scrollMarginTop: 72 },
  h3: { fontSize: 15.5, fontWeight: 700, margin: "18px 0 4px", color: "#111827", scrollMarginTop: 72 },
  p: { fontSize: 14, color: "#4b5563", margin: "6px 0" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13.5, margin: "8px 0" },
  th: { textAlign: "left", padding: "7px 10px", borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap" },
  td: { padding: "7px 10px", borderBottom: "1px solid #f3f4f6", verticalAlign: "top" },
  code: { background: "#f3f4f6", padding: "1px 6px", borderRadius: 4, fontSize: 12.5,
    fontFamily: "ui-monospace,monospace" },
  note: { background: "#f8fafc", borderLeft: "3px solid #94a3b8", padding: "10px 14px",
    fontSize: 13, color: "#475569", borderRadius: 4, margin: "10px 0" },
  tip: { background: "#fef7e0", borderLeft: "3px solid #f0c36d", padding: "10px 14px",
    fontSize: 13, color: "#664d03", borderRadius: 4, margin: "10px 0" },
  flow: { background: "#0f172a", color: "#e2e8f0", padding: 14, borderRadius: 8,
    fontSize: 12.5, overflowX: "auto", whiteSpace: "pre", lineHeight: 1.7 },
  btn: { background: "#111827", color: "#fff", border: "none", borderRadius: 8,
    padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
};

function T({ head, rows }) {
  return (
    <table style={S.table}>
      <thead><tr>{head.map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i}>{r.map((c, j) => <td key={j} style={S.td}>{c}</td>)}</tr>
      ))}</tbody>
    </table>
  );
}

const C = (t) => <code style={S.code}>{t}</code>;

/** 구 단일 페이지 앵커(#sec-N) → 문서 슬러그 — 가이드 QA 근거 칩·구 링크 하위호환. */
export const ANCHOR_TO_DOC = {
  "#sec-1": "intro", "#sec-2": "principle", "#sec-3": "reading",
  "#sec-4": "tools", "#sec-5": "master", "#sec-6": "api-intro", "#sec-7": "feedback",
};

export const GUIDE_DOCS = [
  {
    slug: "intro", group: "시작하기", title: "loginHS 소개",
    body: ({ onNavigate }) => (
      <>
        <h1 style={S.h1} id="sec-1">loginHS 소개</h1>
        <p style={S.p}>
          loginHS 는 품명을 입력하면 <b>HS 세번(HSK 10자리)</b> 을 근거와 함께 찾아주는
          AI 세번분류 서비스입니다. 세번분류는 관세율·요건·FTA 적용의 출발점이지만,
          품명만으로 정답을 단정하기 어려운 경우가 많습니다. loginHS 는{" "}
          <b>"근거 없이 확정하지 않는다"</b> 는 원칙 위에서 관세청 분류사례·관세율표
          원문·AI 의미 분석을 교차 검증해 확신할 수 있는 건은 즉시 확정하고, 애매한 건은
          후보를 나란히 제시합니다.
        </p>
        <T head={["메뉴", "무엇을 하나요"]} rows={[
          ["AI HS 분류", "품명 입력 → AI 가 근거와 함께 세번 확정/후보 제시 (분류 원리·결과 읽는 법 문서 참고)"],
          ["속견표", "부·류 구조를 표로 한눈에 — 어느 류부터 볼지 감 잡기"],
          ["HS 비교", "후보 세번 여러 개를 나란히 놓고 세율·요건 비교"],
          ["문의·건의", "분류 이의·기능 제안 접수"],
        ]} />
      </>
    ),
  },
  {
    slug: "principle", group: "시작하기", title: "AI 세번분류 원리",
    tocItems: [["#g-five", "5단계 증거 투표"], ["#g-gates", "확정 전 이중 게이트"], ["#g-tree-v2", "공식 다단계 프레임 v2"], ["#g-official", "공식명 우선 원칙"], ["#g-hold", "보류는 실패가 아닙니다"]],
    body: () => (
      <>
        <h1 style={S.h1} id="sec-2">AI 세번분류 원리 — 5단계 증거 투표</h1>
        <p style={S.p} id="g-five">
          검색창에 품명을 입력하면(온톨로지 탭) 아래 증거들이 <b>동시에</b> 수집되고,
          각자 "이 세번이 맞다"에 투표합니다. 단일 AI 의 한 마디가 아니라
          서로 독립적인 근거의 합의로 결정되고, 합의가 높아도 확정 직전에
          두 개의 게이트로 다시 검증됩니다.
        </p>
        <div style={S.flow}>{`① 통관 사례 분석    관세청 분류사례(기준)를 가중 우선 — 실통관·해외(참고)는 건수가
                    많아도 기준을 덮지 못하게 물량 영향을 압축합니다
② AI 의미 분석      품명의 의미를 벡터로 변환, 가장 가까운 사례 30건의 세번 분포
③ AI 검토           "관세율표 해석상 합리적인가?" 2차 소견 — 두 AI 모델이 교차검증,
                    결론이 갈리면 상위 모델 의견 채택 (참고층)
④ 관세율표 역검증    후보 세번의 공식 품명에 입력 단어가 실제로 들어있는지 확인
⑤ 신뢰도 합성       ①~④ 가중 합산 → HIGH(확정) / MEDIUM / LOW(보류)`}</div>
        <div style={S.note}>
          AI 검토는 <b>참고 의견</b>입니다 — 최종 세번은 류주(법적 주석)·수치 기준·결정례
          같은 검증 가능한 근거가 판정하며, AI 소견과 조문이 충돌하면 조문이 이깁니다.
        </div>
        <h3 style={S.h3} id="g-gates">확정 전 이중 게이트 — 투표만으로 확정하지 않습니다</h3>
        <div style={S.flow}>{`앵커 게이트     관세청 결정례·사전심사·공식명 같은 "정답 앵커"가 0건이면 투표가
                높아도 HIGH 를 금지하고 MEDIUM(사전심사 권고)으로 낮춥니다 —
                신고이력·AI 신호만으로는 확정하지 않습니다
류주 게이트     확정 후보를 류주·호해설 원문과 대조합니다. 재질·기준으로 갈리면
                갈림 질문을 드리고, 입력에 수치(성분 %·체 통과율 등)가 있으면
                질문 없이 류주 문턱과 대조해 자동 판정합니다`}</div>
        <div style={S.tip}>
          <b>갈림 질문에 답하면 확정으로 바뀝니다</b> — 예: "방사선 방호복"은 재질 질문
          (부직포/가황고무)에 답하는 순간 6210.10 또는 4015.90 으로 확정됩니다. 품명에
          "닭고기 30%, 연어 45%" 처럼 수치를 포함하면 제16류 주 제2호 기준으로
          질문 없이 자동 판정됩니다. 실전 사례는 <b>분류 사례연구</b> 페이지에 모아두었습니다.
        </div>
        <h3 style={S.h3} id="g-tree-v2">공식 다단계 프레임 v2 — 질문은 보강하고, 코드는 바꾸지 않습니다</h3>
        <p style={S.p}>
          loginHS 는 관세율표의 공식 상하위 경로를 <b>HS4 → 5단위 → HS6 → 국내 세분 →
          HSK 10자리</b> 순서로 고정한 1,230개 프레임을 구축했습니다. 이 구조의 27,927개
          노드 가운데 실제 확인이 필요한 5,358개 결정 지점에는 재질·용도·가공상태·성분·수치
          경계를 묻는 질문과 필수·배제 사실을 붙였습니다. 최종 도착점은 11,268개 HSK 10자리입니다.
        </p>
        <div style={S.flow}>{`① 공식 구조 고정      관세율표의 코드·상하위 관계는 AI가 추가·삭제·변경하지 않습니다
② 질문 보강           전용 작성 모델이 각 결정 지점의 객관적 질문·필수 사실·배제 조건을 작성합니다
③ 독립 교차검증       별도의 검증 모델이 공식 edge 누락·중복, 경로 충돌과 질문 오염을 다시 검사합니다
④ 엄격한 승격         pass · 0.90 이상 · node_errors 0인 프레임만 운영 후보가 됩니다`}</div>
        <div style={S.note}>
          공식 다단계 프레임 v2의 <b>전체 1,230개 프레임 작성·교차검증은 완료</b>되었습니다.
          검증 DB는 운영 분류 흐름에 연결되어 있습니다. 이 질문과 추천 세번은 검토를 돕는 자료이며,
          법적 확정이 필요한 경우 관세평가분류원의 품목분류 사전심사를 이용하세요.
        </div>
        <p style={S.p}>
          검색 결과가 하나의 HS4로 좁혀지면 검증된 공식 프레임으로 자동 전환됩니다. 화면에
          표시되는 필수 확인 사실과 제외 조건을 비교해 선택하면, 공식 상하위 경로를 벗어나지
          않은 상태에서 HSK 10자리까지 안내합니다.
        </p>
        <h3 style={S.h3} id="g-official">공식명 우선 원칙</h3>
        <p style={S.p}>
          입력한 품명이 관세율표의 <b>공식 세번명과 정확히 일치</b>하면(띄어쓰기·대소문자 무시)
          사례가 없어도 그 세번을 최우선 후보로 즉시 제시합니다. 공식명 일치는
          "그 이름이 관세율표 어느 호에 사는지"를 알려주는 가장 강한 근거입니다 —
          다만 <b>그 이름의 모든 제품이 그 호라는 뜻은 아니어서</b>, 확정 직전에
          류주 게이트가 재질·구성을 한 번 더 검증합니다.
        </p>
        <div style={S.tip}>
          <b>예: 방사선 방호복</b> — 공식 품명은 4015.90-2000(제40류 = <b>고무로 만든</b> 의류)에
          실존합니다. 전통적 납(연질)고무 방호복이라 여기 이름이 있는 것이고, 같은 이름이라도
          <b> 부직포제</b>라면 제62류로 갈립니다: 가황(납)고무제 → <b>4015.90-2000</b> /
          부직포제 → <b>6210.10-2000</b>(제5603호의 직물제 — 관세청 사전심사 사례 일치).
          그래서 loginHS 는 공식명 확정 카드와 함께 재질 갈림 질문을 드립니다 —
          답하는 순간 재질에 맞는 세번으로 확정됩니다.
        </div>
        <p style={S.p}>
          또한 관세청 분류사례가 공식명과 다른 호를 강하게 지지하면(예: 잠수복)
          확정을 <b>보류</b>하고 두 근거를 나란히 보여드립니다.
        </p>
        <h3 style={S.h3} id="g-hold">보류는 실패가 아닙니다</h3>
        <p style={S.p}>
          재질·구성에 따라 세번이 갈리는 품명을 억지로 확정하면 오분류로 이어집니다.
          loginHS 는 확신이 부족하면 <b>보류</b>하고 후보와 근거를 제시합니다 —
          이때 사람의 판단이 들어가는 것이 정확한 분류입니다.
        </p>
        <div style={S.tip}>
          <b>비슷한 공식명 제안</b> — 확정에 실패한 품명이 관세율표 공식명과 글자가
          비슷하면 "혹시 이 공식 세번명을 찾으셨나요?" 로 제안합니다.
          예: "방사선방화복" 입력 시 "방사선 방호복(4015.90-2000)" 제안. 클릭하면 재검색됩니다.
          자동으로 바꿔 확정하지는 않습니다 — 방호복과 방화복은 다른 물품일 수 있으니까요.
        </div>
      </>
    ),
  },
  {
    slug: "reading", group: "시작하기", title: "결과 화면 읽는 법",
    body: () => (
      <>
        <h1 style={S.h1} id="sec-3">결과 화면 읽는 법</h1>
        <T head={["표시", "의미", "권장 행동"]} rows={[
          [<b>HIGH (내부 근거 강함)</b>, "국내 근거(신고이력·사례·결정례) 합의가 강함 — 최우선 세번", "확정 전 근거 확인 권장"],
          [<b>MEDIUM</b>, "우세 후보는 있으나 확신 부족", "근거 카드 확인 후 판단"],
          [<b>MEDIUM (사전심사 권고)</b>, "신고이력·AI 신호만 있고 관세청 결정례·공식명 앵커가 없음", "중요 품목이면 관세평가분류원 사전심사 검토"],
          [<b>갈림 질문</b>, "재질·기준에 따라 세번이 갈림 — 판별 질문 제시", "질문에 답하면 확정으로 전환"],
          [<b>보류 / LOW</b>, "재질·구성 확인 필요 또는 근거 부족", "후보 카드 비교 + 비슷한 공식명 제안 확인"],
        ]} />
        <p style={S.p}>
          확정 카드에는 결정에 쓰인 근거가 그대로 표시됩니다:
          <b>관세율표 공식명 정확일치</b>(발동 시 첫 카드) · 통관 사례 분석 ·
          AI 의미 분석 · AI 검토 · 관세율표 본문 역검증. 각 항목을 클릭하면
          우측 근거 패널에서 원문(사례·법령·해설)을 확인할 수 있습니다.
        </p>
        <div style={S.note}>
          우측 <b>근거 자료 패널</b>은 결정과 독립적으로 관세청 분류사례 · 관세사 사례 ·
          관련법령 · 호해설을 항상 함께 보여줍니다 — AI 의 결정을 검증하는 용도로 활용하세요.
        </div>
      </>
    ),
  },
  {
    slug: "tools", group: "기능", title: "검색 · 비교 · 속견표",
    body: () => (
      <>
        <h1 style={S.h1} id="sec-4">검색 · 비교 · 속견표</h1>
        <T head={["기능", "이럴 때"]} rows={[
          ["검색 탭", "세번·품명을 이미 알고 원문(세율·요건·해설)을 바로 볼 때"],
          ["다중검색", "여러 품명을 그리드에 놓고 행 클릭으로 하나씩 검토할 때"],
          ["HS 비교", "후보 2~3개의 세율·협정세율을 나란히 비교할 때"],
          ["속견표", "생소한 품목 — 부·류 구조에서 출발점을 찾을 때"],
        ]} />
      </>
    ),
  },
  {
    slug: "master", group: "기능", title: "내 품목 (마스터 등록)",
    body: () => (
      <>
        <h1 style={S.h1} id="sec-5">내 품목 (마스터 등록)</h1>
        <p style={S.p}>
          검토를 거쳐 확정한 품명은 <b>마스터로 등록</b>할 수 있습니다. 등록된 품명은
          다음부터 AI 를 거치지 않고 <b>즉시, 항상 같은 세번</b>으로 응답됩니다 —
          반복 품명이 많을수록 분류가 빨라지고 일관성이 보장됩니다.
          API 대량분류에도 동일하게 적용됩니다.
        </p>
      </>
    ),
  },
  {
    slug: "api-intro", group: "API", title: "API 연동 개요",
    body: ({ onNavigate }) => (
      <>
        <h1 style={S.h1} id="sec-6">API 연동</h1>
        <p style={S.p}>
          사내 ERP·통관 솔루션에서 loginHS 분류를 직접 호출할 수 있습니다.
          품명 목록을 보내면 {C("hsk")} · {C("review_needed")} · 후보 목록이 반환됩니다.
          엔드포인트·요청 형식은 API 문서, 키 발급은 인증·키 발급 문서를 참고하세요.
        </p>
        <button style={S.btn} onClick={() => onNavigate("api-docs")}>API 문서 보기 →</button>
      </>
    ),
  },
  {
    slug: "feedback", group: "지원", title: "문의 · 개선 요청",
    body: ({ onNavigate }) => (
      <>
        <h1 style={S.h1} id="sec-7">문의 · 개선 요청</h1>
        <p style={S.p}>
          분류 결과에 이의가 있거나 기능 제안이 있으시면{" "}
          <a href="/?page=contact" onClick={(e) => { e.preventDefault(); onNavigate("contact"); }}>문의하기</a>로
          알려주세요. 전문가 검토 후 반영되며, 확정된 품명은 마스터에 등록되어
          이후 즉시·일관되게 응답됩니다 — 이의 제기가 서비스 품질을 함께 올립니다.
        </p>
      </>
    ),
  },
];

/** 문서 슬러그로 이동 — 어느 페이지에서든(앱 표준 pushState+popstate 내비게이션). */
export function goGuideDoc(slug) {
  const u = new URL(window.location.href);
  u.searchParams.set("page", "guide");
  u.searchParams.set("doc", slug);
  u.hash = "";
  window.history.pushState({}, "", u.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
}
